import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Rating from '@mui/material/Rating';
import Typography from '@mui/material/Typography';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded';
import { RootState } from '../../redux/store';
import { UserRole } from '../../types/UserRole';
import { colors, layout } from '../../theme/tokens';
import noavatar from '../../public/images/noavatar.png';

/* The frame of every account page: a persistent, role-based navigation rail (a bottom bar on
   mobile) next to the page content. Pages put their second-level navigation in tabs at the top
   of the content, and never render their own sidebars. */

type NavItem = {
    label: string;
    to: string;
    icon: JSX.Element;
    // Active for these path prefixes (defaults to `to`)
    match?: string[];
};

const NAV_ITEMS: Record<string, NavItem[]> = {
    [UserRole.Customer]: [
        {
            label: 'My tests',
            to   : '/account/customer',
            icon : <DashboardOutlinedIcon />,
            match: ['/account/customer/cycle'],
        },
        { label: 'Favorite testers', to: '/account/customer/favorite', icon: <StarOutlineRoundedIcon /> },
        { label: 'Profile', to: '/account/customer/myprofile', icon: <PersonOutlineRoundedIcon /> },
    ],
    [UserRole.Tester]: [
        { label: 'Test cycles', to: '/account/tester', icon: <DashboardOutlinedIcon />, match: ['/account/tester/cycle'] },
        { label: 'Profile', to: '/account/tester/myprofile', icon: <PersonOutlineRoundedIcon /> },
        { label: 'Reviews', to: '/account/tester/reviews', icon: <RateReviewOutlinedIcon /> },
        { label: 'Affiliates', to: '/account/tester/affiliates', icon: <CampaignOutlinedIcon /> },
    ],
};
// Viewers use the customer area
NAV_ITEMS[UserRole.Viewer] = NAV_ITEMS[UserRole.Customer];

const RAIL_WIDTH = 220;

function isActive(item: NavItem, pathname: string): boolean {
    if (pathname === item.to || pathname === `${item.to}/`) {
        return true;
    }
    // The section roots (/account/customer, /account/tester) only match exactly or via `match`
    const prefixes = item.match || (item.to.split('/').length > 3 ? [item.to] : []);
    return prefixes.some((prefix) => pathname.startsWith(prefix));
}

interface Props {
    children: React.ReactNode;
}

const AccountShell: React.FC<Props> = ({ children }) => {
    const history = useHistory();
    const { pathname } = useLocation();
    const { user, resolution } = useSelector((state: RootState) => state);

    const items = NAV_ITEMS[user.role] || [];
    const isMobile = resolution.device === 'mobile' || resolution.device === 'mobile-tablet';
    const isCustomer = user.role === UserRole.Customer || user.role === UserRole.Viewer;

    if (isMobile) {
        const activeIndex = items.findIndex((item) => isActive(item, pathname));
        return (
            <Box className="account-shell" sx={{ pb: 7 }}>
                {children}
                <Paper elevation={3} sx={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1200 }}>
                    <BottomNavigation showLabels value={activeIndex} onChange={(_, index) => history.push(items[index].to)}>
                        {items.map((item) => (
                            <BottomNavigationAction key={item.to} label={item.label} icon={item.icon} />
                        ))}
                    </BottomNavigation>
                </Paper>
            </Box>
        );
    }

    const displayName = isCustomer ? user.companyName || `${user.firstName || ''} ${user.lastName || ''}` : `${user.firstName || ''} ${user.lastName || ''}`;

    return (
        <Box sx={{ display: 'flex', alignItems: 'stretch', minHeight: 'calc(100vh - 97px)', maxWidth: layout.shellMaxWidth, mx: 'auto' }}>
            <Box
                component="nav"
                aria-label="Account navigation"
                sx={{
                    width          : RAIL_WIDTH,
                    flexShrink     : 0,
                    borderRight    : `1px solid ${colors.dividerBlue}`,
                    backgroundColor: '#fafbfd',
                    px             : 1.5,
                    py             : 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1, mb: 2 }}>
                    <Avatar src={user.avatarUrl || noavatar} sx={{ width: 40, height: 40 }} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontWeight: 600, fontSize: 14, color: colors.textDark }}>
                            {displayName.trim() || user.email}
                        </Typography>
                        {isCustomer && user.balance !== undefined && user.balance !== null && (
                            <Typography sx={{ fontSize: 12.5, color: colors.textGrey }}>
                                Balance ${Number(user.balance).toFixed(2)}
                            </Typography>
                        )}
                        {user.role === UserRole.Tester && (
                            <>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Rating value={Number(user.rating) || 0} precision={0.25} readOnly size="small" />
                                    <Typography sx={{ fontSize: 12.5, color: colors.textGrey }}>
                                        {Number(user.rating || 0).toFixed(2)}
                                    </Typography>
                                </Box>
                                {user.addedToFavorites > 0 && (
                                    <Typography sx={{ fontSize: 12, color: colors.textGrey }}>
                                        Added to favorites: {user.addedToFavorites}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Box>
                </Box>

                <List disablePadding>
                    {items.map((item) => {
                        const active = isActive(item, pathname);
                        return (
                            <ListItemButton
                                key={item.to}
                                selected={active}
                                onClick={() => history.push(item.to)}
                                sx={{
                                    borderRadius: 1.5,
                                    mb          : 0.5,
                                    '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: colors.primaryTint, color: colors.primary },
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 36, color: active ? colors.primary : colors.textGrey }}>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14, fontWeight: active ? 600 : 400 }} />
                            </ListItemButton>
                        );
                    })}
                </List>
            </Box>

            <Box component="section" className="account-shell" sx={{ flex: 1, minWidth: 0 }}>
                {children}
            </Box>
        </Box>
    );
};

export default AccountShell;
